import { formatDateMMDDYYYY, capitalizeString, formatNumberToUSD, toTitleCase } from 'utils';
import _chunk from 'lodash/chunk';
import { LINE_ITEM_TYPE, ORDER_CHANNEL, statusCodesMap } from 'constants/index';

export const getPromotionCodes = (promos, promosPerLine = 3) => {
  const hasPromos = typeof promos === 'string' && promos.length;
  const codesArray = (hasPromos && promos.split(',')) || [];

  return _chunk(codesArray, promosPerLine);
};

export const getGiftWrapCost = (items = []) => {
  const totalCost = items
    .reduce((acc, { giftWrapCode, giftTotal }) => (giftWrapCode ? acc + Number(giftTotal) : acc), 0)
    .toFixed(2);
  return totalCost > 0 ? `$${totalCost}` : '';
};

export const calculateDelivery = (deliveryItems = []) => {
  const itemsReducer = (acc, { price: { itemDeliveryPrice, orderPrice = 0 }, quantity = 1 }) => {
    return acc + quantity * (itemDeliveryPrice || orderPrice);
  };
  return deliveryItems.reduce(itemsReducer, 0).toFixed(2);
};

export const getFailedOrderDeliveryCost = (items = []) =>
  items.reduce((acc, { freight }) => acc + Number(freight), 0).toFixed(2);

export const isInStoreOrder = (channel = '') => capitalizeString(channel) === ORDER_CHANNEL.inStore;
export const isConnectOrder = (channel = '') => capitalizeString(channel) === ORDER_CHANNEL.connect;
export const isCmosOrder = (channel = '') => channel.toUpperCase() === ORDER_CHANNEL.cmos;

const getChannel = ({ channel, orderId, brandLine }) => {
  if (isCmosOrder(channel)) {
    return ORDER_CHANNEL.cmos;
  }

  const capChannel = capitalizeString(channel);
  return capChannel === ORDER_CHANNEL.inStore
    ? `${brandLine} Store ${orderId.split(':')[0]}`
    : capChannel || ORDER_CHANNEL.online;
};

export const getOrderPrices = (order, isFailedOrder) => {
  const {
    orderDetails,
    subtotal,
    totalStateTaxAmount = 0,
    totalCountyTaxAmount = 0,
    totalCityTaxAmount = 0,
    totalLocalTaxAmount = 0,
    totalOrderAmount,
    promotionsApplied,
  } = order;
  const items = orderDetails[0]?.items || [];
  let totalTax =
    Number(totalStateTaxAmount) +
    Number(totalCountyTaxAmount) +
    Number(totalCityTaxAmount) +
    Number(totalLocalTaxAmount);

  // If the tax values did not come from the server, calculate them
  // This seems to happen when there are only RETURN items
  // But there are tax values when there are RETURN and TAKE items in the same order
  if (!totalTax) {
    items.forEach(({ transactionType, tax }) => {
      if (statusCodesMap[transactionType]?.isNegative) {
        totalTax -= Number(tax);
      } else {
        totalTax += Number(tax);
      }
    });
  }

  const deliveryItems = items.filter(
    (item) => item.serviceName?.toUpperCase().includes(LINE_ITEM_TYPE.DELIVERY) || Number(item.freight) > 0,
  );
  const deliveryChargesField = deliveryItems.length
    ? {
        deliveryCharges: isFailedOrder
          ? `$${getFailedOrderDeliveryCost(items)}`
          : `$${calculateDelivery(deliveryItems)}`,
      }
    : { deliveryCharges: '$0.00' };

  return {
    giftPackaging: isFailedOrder && getGiftWrapCost(items),
    promotion: isFailedOrder && getPromotionCodes(promotionsApplied),
    subTotalPrice: formatNumberToUSD(Number(subtotal)),
    ...deliveryChargesField,
    orderTotalTax: formatNumberToUSD(totalTax),
    orderTotalAmt: formatNumberToUSD(Number(totalOrderAmount)),
  };
};

export const parseOrderDetails = (item) => ({
  client: toTitleCase(`${item.soldTo?.firstName} ${item.soldTo?.lastName}`),
  'order#': item.formattedOrderId || item.orderId || '',
  date: item.orderDate ? formatDateMMDDYYYY(item.orderDate) : '',
  channel: getChannel(item),
  total: formatNumberToUSD(parseFloat(item.totalOrderAmount)),
});

export const parseShippingData = (address = {}) => {
  return {
    ...address,
    firstName: capitalizeString(address.firstName),
    lastName: capitalizeString(address.lastName),
    city: capitalizeString(address.city),
    line1: capitalizeString(address.line1),
    line2: capitalizeString(address.line2),
  };
};

export const parseIfFailedPickupOrder = (items = [], shipmentMethod = '') =>
  items.filter((item) => item?.shipToStore || item?.storePickupFlag).length ? 'Curbside Pickup' : shipmentMethod;

export const validateShippingAddress = (address) => {
  const { firstName, lastName, city, line1 } = address || {};
  return !!firstName && !!lastName && !!city && !!line1;
};
